import React, { useState } from 'react';
import {
    Card,
    CardContent,
    Typography,
    Tabs,
    Tab,
    Box,
} from '@mui/material';
import {
    BarChart,
    Bar,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    ResponsiveContainer,
    ReferenceLine,
    Label
} from 'recharts';

const TabPanel = ({ children, value, index }) => (
    <div role="tabpanel" hidden={value !== index}>
        {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
);

const SecondaryCharts = ({ aggregated, heatmapData, spyPrice }) => {
    const [tabValue, setTabValue] = useState(0);

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    // Prepare data for charts
    const chartData = aggregated
        .map((item) => ({
            strike: item.strike,
            gex: item.total_gex,
        }))
        .sort((a, b) => a.strike - b.strike); // FIX 1: Sort ascending

    // Calculate cumulative GEX
    let cumulative = 0;
    const cumulativeData = [...chartData].map((item) => {
        cumulative += item.gex;
        return {
            strike: item.strike,
            cumulative,
        };
    });

    // FIX: Calculate symmetric domain for proper visual centering
    const maxAbsCumulative = Math.max(
        ...cumulativeData.map((item) => Math.abs(item.cumulative))
    );
    // Round scaling limit up to nearest 100M for cleaner ticks
    const domainLimit = Math.ceil((maxAbsCumulative * 1.1) / 1e8) * 1e8;

    // Generate explicit ticks: -limit, -limit/2, 0, limit/2, limit
    const yTicks = [
        -domainLimit,
        -domainLimit / 2,
        0,
        domainLimit / 2,
        domainLimit
    ];

    const formatGEX = (value) => {
        const billions = value / 1e9;
        return `$${billions.toFixed(2)}B`;
    };

    return (
        <Card>
            <CardContent>
                <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                    📊 Analysis Views
                </Typography>

                <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2 }}>
                    <Tab label="GEX by Strike" />
                    <Tab label="Cumulative GEX" />
                </Tabs>

                <TabPanel value={tabValue} index={0}>
                    <ResponsiveContainer width="100%" height={450}>
                        <BarChart
                            data={chartData}
                            margin={{ top: 30, right: 30, left: 90, bottom: 5 }} // FIX 2: Max margin
                        >
                            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                            <XAxis
                                dataKey="strike"
                                type="category" // FIX 4: Explicitly enforce same type
                                stroke="#94a3b8"
                                label={{ value: 'Strike', position: 'insideBottom', offset: -5 }}
                            />
                            <YAxis
                                stroke="#94a3b8"
                                tickFormatter={formatGEX}
                            >
                                <Label
                                    value="Net GEX"
                                    angle={-90}
                                    position="insideLeft"
                                    offset={0}
                                    dx={-40} // FIX 3: Direct prop, strong push left
                                    style={{ fill: '#94a3b8', textAnchor: 'middle', fontWeight: 600 }}
                                />
                            </YAxis>
                            <RechartsTooltip
                                cursor={{ fill: 'transparent' }}
                                contentStyle={{
                                    backgroundColor: '#0f172a',
                                    border: '1px solid #334155',
                                    borderRadius: '8px',
                                }}
                                formatter={(value) => [formatGEX(value), 'Net GEX']}
                            />
                            <ReferenceLine
                                x={Math.round(spyPrice)}
                                stroke="#fbbf24"
                                strokeDasharray="3 3"
                            >
                                <Label
                                    value={`SPOT: $${spyPrice.toFixed(2)}`}
                                    position="top"
                                    fill="#fbbf24"
                                    fontSize={12}
                                    fontWeight={700}
                                    offset={10}
                                />
                            </ReferenceLine>
                            <ReferenceLine y={0} stroke="#475569" strokeDasharray="3 3" />
                            <Bar
                                dataKey="gex"
                                fill="#3b82f6"
                                name="Net GEX"
                                activeBar={{ fill: '#60a5fa' }}
                            />
                        </BarChart>
                    </ResponsiveContainer>
                </TabPanel>

                <TabPanel value={tabValue} index={1}>
                    <ResponsiveContainer width="100%" height={450}>
                        <LineChart
                            data={cumulativeData}
                            margin={{ top: 30, right: 30, left: 90, bottom: 5 }}
                        >
                            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                            <XAxis
                                dataKey="strike"
                                type="category" // FIX 4: Explicitly enforce same type
                                stroke="#94a3b8"
                                label={{ value: 'Strike', position: 'insideBottom', offset: -5 }}
                            />
                            <YAxis
                                stroke="#94a3b8"
                                tickFormatter={formatGEX}
                                domain={[-domainLimit, domainLimit]} // FIX: Force symmetric domain
                                ticks={yTicks} // FIX 5: Force explicit 0 tick
                                interval={0} // Ensure all ticks show
                            >
                                <Label
                                    value="Cumulative GEX"
                                    angle={-90}
                                    position="insideLeft"
                                    offset={0}
                                    dx={-40}
                                    style={{ fill: '#94a3b8', textAnchor: 'middle', fontWeight: 600 }}
                                />
                            </YAxis>
                            <RechartsTooltip
                                contentStyle={{
                                    backgroundColor: '#0f172a',
                                    border: '1px solid #334155',
                                    borderRadius: '8px',
                                }}
                                formatter={(value) => [formatGEX(value), 'Cumulative GEX']}
                            />
                            <ReferenceLine
                                x={Math.round(spyPrice)}
                                stroke="#fbbf24"
                                strokeDasharray="3 3"
                            >
                                <Label
                                    value={`SPOT: $${spyPrice.toFixed(2)}`}
                                    position="top"
                                    fill="#fbbf24"
                                    fontSize={12}
                                    fontWeight={700}
                                    offset={10}
                                />
                            </ReferenceLine>
                            <ReferenceLine y={0} stroke="#94a3b8" strokeDasharray="3 3" strokeWidth={2} /> {/* Zero Line */}
                            <Line type="monotone" dataKey="cumulative" stroke="#8b5cf6" strokeWidth={2} dot={false} name="Cumulative GEX" />
                        </LineChart>
                    </ResponsiveContainer>
                </TabPanel>
            </CardContent>
        </Card>
    );
};

export default SecondaryCharts;
